import { useEffect, useRef, useState, useMemo } from 'react';
import * as DateFns from 'date-fns';
import { useParams, useLocation, useHistory, useRouteMatch } from 'react-router-dom';
import { routeToEventType, routeToScope, routeToSpecificScope, routeToTableSpecificScope, routeToViewPracticeGameFilterForceScope, routeToViewPracticeGamePageScope } from './helpersts';
import { Manage } from "../config/commonTypes";
import { PlayersExcludedByPeriodType } from "../store/types";

type IntervalFunction = () => ( unknown | void );

//https://overreacted.io/making-setinterval-declarative-with-react-hooks/
export function useInterval(callback: IntervalFunction, delay: number | null) {

  const savedCallback = useRef<IntervalFunction>();
  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      if (savedCallback.current) {
        savedCallback.current();
      }
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

type EventFunction = (event: Event) => void;

// Extracted from: https://usehooks.com/useEventListener/
export const useEventListener = (eventName:string, handler:EventFunction, element = window) => {
  // Create a ref that stores handler
  const savedHandler = useRef<EventFunction>();

  // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(
    () => {
      // Make sure element supports addEventListener
      const isSupported = element && element.addEventListener;
      if (!isSupported) return;

      // Create event listener that calls handler function stored in ref
      const eventListener = (event: Event) => {
        if (savedHandler.current) {
         savedHandler.current(event);
        }
      };

      // Add event listener
      element.addEventListener(eventName, eventListener);

      // Remove event listener on cleanup
      return () => {
        element.removeEventListener(eventName, eventListener);
      };
    },
    [eventName, element] // Re-run if eventName or element changes
  );
};

// T gets the type of the calling function and returns the same type
// https://www.typescriptlang.org/docs/handbook/generics.html <---
export const usePrevious = <T>(value: T): T => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current as T;
};

// Schedule hook to get certain dates
export const useSchedule = (selectedDate:Date) => {
  const prevDate = usePrevious(selectedDate);
  const [scheduleDates, setScheduleDates] = useState<Date[]>([]);

  useEffect(() => {
    if (!DateFns.isEqual(selectedDate, prevDate)) {
      const monthStart = DateFns.startOfMonth(selectedDate);
      const startDate = DateFns.startOfWeek(DateFns.startOfMonth(selectedDate));
      const endDate = DateFns.endOfWeek(DateFns.endOfMonth(selectedDate));
      setScheduleDates([monthStart, startDate, endDate, new Date()]);
    }
  }, [selectedDate]);

  return scheduleDates;
};

interface Router {
  match?: {};
  location?: {};
  params?: {};
  history?: {};
}

// https://usehooks.com/useRouter/
export const useRouter = <T extends Router>() => {
  const params = useParams<T["params"] & {}>();
  const location = useLocation<T["location"]>();
  const history = useHistory<T["history"]>();
  const match = useRouteMatch<T["match"] & {}>();

  // Return our custom router object
  // Memoize so that a new object is only returned if something changes
  return useMemo(() => {
    return {
      // For convenience add push(), replace(), pathname at top level
      push: history.push,
      replace: history.replace,
      pathname: location.pathname,
      search: location.search,
      // Include the custom Starburst scopes so we have access to
      // call it on the React components more easily
      scope: routeToScope(location.pathname),
      eventType: routeToEventType(location.pathname),
      specificScope: routeToSpecificScope(location.pathname),
      viewPracticeGamePageScope: routeToViewPracticeGamePageScope(location.pathname),
      tableSpecificScope: routeToTableSpecificScope(location.pathname),
      forcedScopeForFilter: routeToViewPracticeGameFilterForceScope(location.pathname),
      // Include match, location, history objects so we have
      // access to extra React Router functionality if needed.
      match,
      location,
      history,
      params
    };
  }, [params, match, location, history]);
};

export const useExcludePlayers = (
  currentPractice: Manage,
  gameId: string,
  excludedPlayersByPeriod: PlayersExcludedByPeriodType[],
  savePlayersExcludedByPeriod: (
    dsid: string,
    players: PlayersExcludedByPeriodType[]
  ) => void
) => {
  const [showExcludePlayersModal, setShowExcludePlayersModal] =
    useState<boolean>(false);
  const [currentPeriodId, setCurrentPeriodId] = useState<number>(1);
  const [currentPeriodName, setCurrentPeriodName] = useState<string>('');

  const onOpenExcludePlayersModal = (sessionId: number, periodName: string) => {
    setCurrentPeriodId(sessionId);
    setCurrentPeriodName(periodName);
    setShowExcludePlayersModal(true);
  };

  const onCloseExcludePlayersModal = () => {
    setShowExcludePlayersModal(false);
  };

  const onApplyExcludePlayersModal = (
    updatedExcludedPlayers: PlayersExcludedByPeriodType[]
  ) => {
    console.log('onApplyExcludePlayersModal', gameId, updatedExcludedPlayers);
    savePlayersExcludedByPeriod(gameId, updatedExcludedPlayers);
  };

  const excludedCountByPeriod = useMemo(() => {
    const drillCount = currentPractice?.drills?.length ?? 0;
    const excludedByPeriod = new Array(drillCount).fill(0);

    if (!Array.isArray(excludedPlayersByPeriod)) {
      return excludedByPeriod;
    }

    excludedPlayersByPeriod.forEach(
      (excludedPlayer: PlayersExcludedByPeriodType) => {
        Object.values(excludedPlayer).forEach((periods) => {
          periods.forEach((period) => {
            const idx = period - 1;
            if (idx >= 0 && idx < excludedByPeriod.length) {
              excludedByPeriod[idx] += 1;
            }
          });
        });
      }
    );

    return excludedByPeriod;
  }, [excludedPlayersByPeriod, currentPractice?.drills?.length]);

  return {
    currentPeriodId,
    setCurrentPeriodId,
    currentPeriodName,
    showExcludePlayersModal,
    onOpenExcludePlayersModal,
    onCloseExcludePlayersModal,
    onApplyExcludePlayersModal,
    excludedCountByPeriod,
  };
};
